/**
 * Iter 110t — Client quick-nav hover panel.
 *
 * Wraps the AppShell "Clients" NavItem so that hovering anywhere on
 * either the nav row OR the panel itself keeps the panel open. Renders
 * pinned clients (top 5) above a recently-opened list (top 12), each
 * row linking to `/clients/:id`. Pin icons inside the panel toggle the
 * pin state via the backend.
 *
 * State / API:
 *   - `GET  /api/me/client-nav`        → initial + refresh after a pin toggle
 *   - `POST /api/me/client-pin`        → toggle pin
 *   - `POST /api/me/client-visit`      → fired by ClientDetail.jsx on mount,
 *                                        so we don't drive that from here
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Pin, PinOff, ArrowRight } from "lucide-react";

export default function ClientNavHoverPanel({ children }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ pinned: [], recent: [] });
  const [loaded, setLoaded] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/me/client-nav");
      setData(r.data || { pinned: [], recent: [] });
      setLoaded(true);
    } catch { /* non-critical */ }
  };

  // Pre-fetch once on first render so the panel paints instantly on the
  // very first hover (avoids a ~150 ms loader flash that would feel
  // janky for an "instant" nav UX).
  useEffect(() => { load(); }, []);

  const onEnter = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    if (!loaded) load();
    // Position relative to the wrapper's viewport bounds so we escape the
    // sidebar's `overflow-y-auto` clipping. Re-measured on every enter so
    // the panel follows scroll/resize naturally.
    if (wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setPos({ top: r.top, left: r.right + 8 });
    }
    setOpen(true);
  };
  const onLeave = () => {
    // 200 ms grace so the mouse can travel from the nav row to the
    // popped-out panel without the panel disappearing under it.
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };

  const togglePin = async (clientId, currentlyPinned) => {
    // Optimistic: flip in local state immediately, then reconcile.
    setData((d) => {
      if (currentlyPinned) {
        return { ...d, pinned: d.pinned.filter((c) => c.client_id !== clientId) };
      }
      const found = (d.recent.find((c) => c.client_id === clientId)) || (d.pinned.find((c) => c.client_id === clientId));
      const nextPinned = [found, ...d.pinned.filter((c) => c.client_id !== clientId)].filter(Boolean).slice(0, 5);
      return { ...d, pinned: nextPinned };
    });
    try {
      await api.post("/me/client-pin", { client_id: clientId, pinned: !currentlyPinned });
      load();
    } catch { load(); }
  };

  return (
    <div ref={wrapRef} className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave} data-testid="client-nav-hover-wrapper">
      {children}
      {open && (
        <div
          style={{ position: "fixed", top: pos.top, left: pos.left }}
          className="w-72 bg-white border border-slate-200 rounded-md shadow-xl z-50 overflow-hidden"
          data-testid="client-nav-hover-panel"
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
        >
          {data.pinned.length > 0 && (
            <div className="border-b border-slate-100">
              <div className="px-3 pt-2.5 pb-1 text-[10px] uppercase tracking-[0.18em] text-slate-400">Pinned</div>
              <ul className="pb-1">
                {data.pinned.map((c) => (
                  <ClientNavRow key={`pin-${c.client_id}`} client={c} pinned onTogglePin={togglePin}/>
                ))}
              </ul>
            </div>
          )}
          {data.recent.length > 0 && (
            <div>
              <div className="px-3 pt-2.5 pb-1 text-[10px] uppercase tracking-[0.18em] text-slate-400">Recently opened</div>
              <ul className="pb-1">
                {data.recent
                  .filter((c) => !data.pinned.some((p) => p.client_id === c.client_id))
                  .slice(0, 8)
                  .map((c) => (
                    <ClientNavRow key={`recent-${c.client_id}`} client={c} pinned={false} onTogglePin={togglePin}/>
                  ))}
              </ul>
            </div>
          )}
          {data.pinned.length === 0 && data.recent.length === 0 && (
            <div className="px-3 py-4 text-xs text-slate-400 italic" data-testid="client-nav-empty">
              No clients opened yet — open one from the Clients page and it&apos;ll show up here for quick access.
            </div>
          )}
          <Link
            to="/clients"
            className="flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 border-t border-slate-100"
            data-testid="client-nav-view-all"
            onClick={() => setOpen(false)}
          >
            View all clients
            <ArrowRight className="h-3 w-3 text-slate-400"/>
          </Link>
        </div>
      )}
    </div>
  );
}

function ClientNavRow({ client, pinned, onTogglePin }) {
  return (
    <li className="flex items-center" data-testid={`client-nav-row-${client.client_id}`}>
      <Link
        to={`/clients/${client.client_id}`}
        className="flex-1 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 truncate"
        title={client.name}
      >
        <span className="font-medium text-slate-800">{client.name}</span>
        {client.alias && <span className="ml-1.5 text-[10px] text-slate-400 lowercase">{client.alias}</span>}
      </Link>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePin(client.client_id, pinned); }}
        className="px-2 py-1.5 text-slate-300 hover:text-amber-500"
        title={pinned ? "Unpin" : "Pin to top"}
        data-testid={`client-nav-pin-${client.client_id}`}
      >
        {pinned ? <Pin className="h-3.5 w-3.5 fill-amber-400 text-amber-400"/> : <PinOff className="h-3.5 w-3.5"/>}
      </button>
    </li>
  );
}
